import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useSnackbar } from 'notistack'
import React, { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  IPhotoUpload,
  IQuery,
  IQueryGenerateUploadUrlsArgs
} from 'src/types/graphql'
import { fetchWithTimeout } from 'src/utils'

const GENERATE_UPLOAD_URLS = gql`
  query GenerateUploadUrls($count: Int!, $nonPublic: Boolean!, $name: String) {
    generateUploadUrls(count: $count, nonPublic: $nonPublic, name: $name) {
      id
      uploadUrl
      signedUrl
    }
  }
`

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'text/xml',
  'application/xml',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'image/jpeg',
  'image/png',
  'image/webp'
]

const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.xml',
  '.xlsx',
  '.xls',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp'
]

export interface IUploadedFile {
  url: string
  signedUrl: string
  id: string
  fileName: string
  fileType: string
  fileSize: number
}

function useUploadFiles(): {
  ref: RefObject<HTMLInputElement | null>
  loading: boolean
  uploadFiles: (files: File[]) => Promise<IUploadedFile[]>
  onSelectFiles: (
    event: React.ChangeEvent<HTMLInputElement>,
    onUploaded: (files: IUploadedFile[]) => void
  ) => Promise<void>
  acceptedExtensions: string[]
} {
  const logger = useLogger()
  const { enqueueSnackbar } = useSnackbar()
  const ref = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const [generateUploadUrls] = useLazyQuery<
    Pick<IQuery, 'generateUploadUrls'>,
    IQueryGenerateUploadUrlsArgs
  >(GENERATE_UPLOAD_URLS, { fetchPolicy: 'network-only' })

  const uploadFiles = useCallback(
    async (files: File[]): Promise<IUploadedFile[]> => {
      const validFiles = files.filter((file) => {
        const supported = ACCEPTED_MIME_TYPES.includes(file.type)
        if (!supported) {
          enqueueSnackbar(`Tipo de archivo no soportado: ${file.name}`, {
            variant: 'warning'
          })
        }
        return supported
      })

      if (validFiles.length === 0) return []

      setLoading(true)

      try {
        const { data, error } = await generateUploadUrls({
          variables: {
            count: validFiles.length,
            nonPublic: true
          }
        })

        if (error || !data?.generateUploadUrls) {
          logger.error('Error retrieving upload urls', { error })
          enqueueSnackbar('Error al obtener URLs de carga', {
            variant: 'error'
          })
          return []
        }

        const urls: IPhotoUpload[] = data.generateUploadUrls

        const uploads = validFiles.map(async (file, i) => {
          const { id, uploadUrl, signedUrl } = urls[i]

          await fetchWithTimeout(uploadUrl, {
            timeout: 30000,
            method: 'PUT',
            headers: {
              'Content-Type': file.type || 'application/octet-stream'
            },
            body: file
          })

          const url = uploadUrl.split('?')[0]

          return {
            url,
            signedUrl: signedUrl ?? '',
            id,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size
          } satisfies IUploadedFile
        })

        return await Promise.all(uploads)
      } catch (error) {
        logger.error('Error uploading files', { error, files })
        enqueueSnackbar('Error al subir archivos', { variant: 'error' })
        return []
      } finally {
        setLoading(false)
      }
    },
    [generateUploadUrls, enqueueSnackbar, logger]
  )

  const onSelectFiles = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      onUploaded: (files: IUploadedFile[]) => void
    ): Promise<void> => {
      const fileList = event.target.files
      if (!fileList || fileList.length === 0) return

      const files = Array.from(fileList)
      const uploaded = await uploadFiles(files)

      if (uploaded.length > 0) {
        onUploaded(uploaded)
      }

      // Reset input so the same file can be re-selected
      event.target.value = ''
    },
    [uploadFiles]
  )

  return useMemo(
    () => ({
      ref,
      loading,
      uploadFiles,
      onSelectFiles,
      acceptedExtensions: ACCEPTED_EXTENSIONS
    }),
    [loading, uploadFiles, onSelectFiles]
  )
}

export default useUploadFiles
